import { useId, type ReactNode } from 'react';

import Text from '@/components/ui/text';
import { Checkbox } from '@/components/ui/checkbox';
import {
  type FormSpacing,
  formSpacing,
  validationTextClasses,
} from '@/theme/size-tokens';

export type FormCheckboxProps = {
  /** Checked state (controlled) */
  checked?: boolean;
  /** Called with the new checked value */
  onCheckedChange?: (checked: boolean) => void;
  /** Legacy onChange handler for backward compat */
  onChange?: (e: { target: { checked: boolean } }) => void;
  /** Disables checkbox interaction */
  disabled?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Marks the checkbox as required */
  required?: boolean;
  /** Label text displayed next to the checkbox */
  label: string;
  /** Helper text displayed below the checkbox */
  helperText?: string;
  /** Error message to display */
  error?: string;
  /** HTML id attribute for the checkbox */
  id?: string;
  /** Spacing between checkbox/label and helper text */
  spacing?: FormSpacing;
  /** Associates the checkbox with additional descriptive text */
  'aria-describedby'?: string;
  /** Overrides the accessible name announced by screen readers */
  'aria-label'?: string;
  /** Optional content rendered to the right of the label (e.g. an info tooltip) */
  labelSuffix?: ReactNode;
};

function FormCheckbox({
  label,
  helperText,
  error,
  required = false,
  spacing = 'xs',
  id: providedId,
  disabled,
  className,
  checked,
  onCheckedChange,
  onChange,
  'aria-describedby': userAriaDescribedby,
  'aria-label': ariaLabel,
  labelSuffix,
}: Readonly<FormCheckboxProps>) {
  const autoId = useId();
  const inputId = providedId ?? autoId;
  const messageId = `${inputId}-message`;
  const hasMessage = !!(error ?? helperText);

  const ariaDescribedby =
    [hasMessage ? messageId : undefined, userAriaDescribedby]
      .filter(Boolean)
      .join(' ') || undefined;

  const handleCheckedChange = (value: boolean) => {
    onCheckedChange?.(value);
    onChange?.({ target: { checked: value } });
  };

  return (
    <div className={`flex flex-col ${formSpacing[spacing]}`}>
      <div className="inline-flex w-fit items-center gap-2">
        <label
          htmlFor={inputId}
          className="text-foreground inline-flex items-center gap-2 text-sm"
        >
          <Checkbox
            id={inputId}
            checked={checked}
            onCheckedChange={handleCheckedChange}
            disabled={disabled}
            className={className}
            required={required}
            aria-label={ariaLabel}
            aria-describedby={ariaDescribedby}
          />
          <Text
            variant={disabled ? 'muted' : 'body'}
            size="sm"
            as="span"
            required={required}
          >
            {label}
          </Text>
        </label>
        {labelSuffix}
      </div>

      {hasMessage && (
        <p
          id={messageId}
          className={`text-sm ${error ? validationTextClasses.error : validationTextClasses.neutral}`}
          aria-live="polite"
        >
          {error ?? helperText}
        </p>
      )}
    </div>
  );
}

FormCheckbox.displayName = 'FormCheckbox';

export default FormCheckbox;
